import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Grid,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import type {
  StaffSalaryConfig,
  CreateStaffSalaryConfigDto,
  UpdateStaffSalaryConfigDto,
  RecurringFrequency,
  Location,
  User,
} from '../../types';
import { RecurringFrequencyLabels } from '../../types';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
  }).format(amount);
};

interface StaffSalaryConfigModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  userId: string;
  baseSalary: number;
  paymentFrequency: RecurringFrequency;
  effectiveDate: string;
  locationId: string;
}

const defaultFormValues: FormData = {
  userId: '',
  baseSalary: 0,
  paymentFrequency: 'monthly',
  effectiveDate: new Date().toISOString().split('T')[0],
  locationId: '',
};

const fetchSalaryConfigs = async (): Promise<StaffSalaryConfig[]> => {
  const response = await axiosInstance.get('/expenses/salary-configs');
  return Array.isArray(response.data) ? response.data : response.data.items || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchStaff = async (): Promise<User[]> => {
  const response = await axiosInstance.get('/users/staff', { params: { limit: 100 } });
  // Handle paginated response format: { data: User[], total: number }
  return Array.isArray(response.data) ? response.data : response.data.data || response.data.items || [];
};

export const StaffSalaryConfigModal = ({ open, onClose }: StaffSalaryConfigModalProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const [editingConfig, setEditingConfig] = useState<StaffSalaryConfig | null>(null);
  const [showForm, setShowForm] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: defaultFormValues,
  });

  // Fetch data
  const { data: configs, isLoading: configsLoading } = useQuery({
    queryKey: ['salary-configs'],
    queryFn: fetchSalaryConfigs,
    enabled: open,
  });

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    enabled: open,
  });

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: fetchStaff,
    enabled: open,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: CreateStaffSalaryConfigDto) => {
      const response = await axiosInstance.post('/expenses/salary-configs', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-configs'] });
      showMessage('Configuration créée avec succès', 'success');
      handleCancelForm();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showMessage(message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateStaffSalaryConfigDto }) => {
      const response = await axiosInstance.patch(`/expenses/salary-configs/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-configs'] });
      showMessage('Configuration mise à jour', 'success');
      handleCancelForm();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      showMessage(message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/expenses/salary-configs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-configs'] });
      showMessage('Configuration supprimée', 'success');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showMessage(message, 'error');
    },
  });

  useEffect(() => {
    if (editingConfig) {
      reset({
        userId: editingConfig.userId,
        baseSalary: editingConfig.baseSalary,
        paymentFrequency: editingConfig.paymentFrequency,
        effectiveDate: editingConfig.effectiveDate.split('T')[0],
        locationId: editingConfig.locationId || '',
      });
      setShowForm(true);
    }
  }, [editingConfig, reset]);

  const handleAddNew = () => {
    setEditingConfig(null);
    reset(defaultFormValues);
    setShowForm(true);
  };

  const handleEdit = (config: StaffSalaryConfig) => {
    setEditingConfig(config);
  };

  const handleDelete = async (config: StaffSalaryConfig) => {
    if (window.confirm(`Supprimer la configuration de ${config.user?.firstName} ${config.user?.lastName} ?`)) {
      await deleteMutation.mutateAsync(config.id);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingConfig(null);
    reset(defaultFormValues);
  };

  const onSubmit = async (data: FormData) => {
    const dto: CreateStaffSalaryConfigDto = {
      userId: data.userId,
      baseSalary: data.baseSalary,
      paymentFrequency: data.paymentFrequency,
      effectiveDate: data.effectiveDate,
      locationId: data.locationId || undefined,
    };

    if (editingConfig) {
      await updateMutation.mutateAsync({ id: editingConfig.id, data: dto });
    } else {
      await createMutation.mutateAsync(dto);
    }
  };

  // Get staff without existing config
  const availableStaff = staff?.filter(
    (user) => !configs?.some((c) => c.userId === user.id && !c.endDate)
  );

  const isLoading =
    configsLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Configuration des salaires</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Form Section */}
        {showForm && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {editingConfig ? 'Modifier la configuration' : 'Nouvelle configuration'}
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="userId"
                    control={control}
                    rules={{ required: "L'employé est requis" }}
                    render={({ field }) => (
                      <FormControl fullWidth required size="small" error={!!errors.userId}>
                        <InputLabel>Employé</InputLabel>
                        <Select
                          {...field}
                          label="Employé"
                          disabled={isLoading || !!editingConfig}
                        >
                          {(editingConfig ? staff : availableStaff)?.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.firstName} {user.lastName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="baseSalary"
                    control={control}
                    rules={{
                      required: 'Le salaire est requis',
                      min: { value: 1, message: 'Le salaire doit être positif' },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Salaire de base"
                        type="number"
                        size="small"
                        fullWidth
                        required
                        error={!!errors.baseSalary}
                        helperText={errors.baseSalary?.message}
                        disabled={isLoading}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">DZD</InputAdornment>,
                        }}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="paymentFrequency"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Fréquence</InputLabel>
                        <Select {...field} label="Fréquence" disabled={isLoading}>
                          {Object.entries(RecurringFrequencyLabels).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="effectiveDate"
                    control={control}
                    rules={{ required: 'La date est requise' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Date d'effet"
                        type="date"
                        size="small"
                        fullWidth
                        required
                        error={!!errors.effectiveDate}
                        disabled={isLoading}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name="locationId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Emplacement</InputLabel>
                        <Select {...field} label="Emplacement" disabled={isLoading}>
                          <MenuItem value="">Non spécifié</MenuItem>
                          {locations?.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={handleCancelForm} disabled={isLoading}>
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      startIcon={isLoading ? <CircularProgress size={16} /> : null}
                    >
                      {editingConfig ? 'Mettre à jour' : 'Créer'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
            <Divider sx={{ mt: 3 }} />
          </Box>
        )}

        {/* Add button */}
        {!showForm && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              disabled={isLoading}
            >
              Ajouter une configuration
            </Button>
          </Box>
        )}

        {/* Configs Table */}
        {configsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : configs && configs.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employé</TableCell>
                  <TableCell align="right">Salaire</TableCell>
                  <TableCell>Fréquence</TableCell>
                  <TableCell>Date d'effet</TableCell>
                  <TableCell>Emplacement</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {config.user?.firstName} {config.user?.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(config.baseSalary)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {RecurringFrequencyLabels[config.paymentFrequency]}
                    </TableCell>
                    <TableCell>
                      {new Date(config.effectiveDate).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      {config.location?.name || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(config)}
                        disabled={isLoading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(config)}
                        disabled={isLoading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Aucune configuration de salaire trouvée.
            <br />
            Ajoutez des configurations pour pouvoir générer les salaires automatiquement.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};
